/**
 * TipsManagerCard (#1720, epic #1589) - admin console section for the
 * feature-discovery tips: full lifecycle (add / edit / enable / delete),
 * per-tip dismissal stats rendered dismissed/total, global dismissal reset
 * behind a confirm, and the rotation settings (displaySeconds,
 * intervalMinutes) plus a separate Email Summary Config card owning the
 * summary-email subject. English fields only - translation happens
 * server-side on save (#1719). Admin-only surface, so the internal word
 * "Tips" may appear here (never in end-user copy). Both cards save through
 * the same PUT /api/admin/tips/config (the endpoint takes all three fields),
 * so either Save button persists the full settings state.
 *
 * The enabled toggle round-trips the full English copy through
 * PUT /api/admin/tips/{tipId}, which re-translates the tip - a known,
 * accepted cost (no partial-update endpoint by design, KISS).
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  IconButton,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
  Stack,
} from '@mui/material';
import { IconPencil, IconTrash, IconRestore } from '@tabler/icons-react';
import { apiGlobal } from '../../services/api';
import { useToast } from '../ToastNotification';
import { SectionContainer } from '../ds';

const errMessage = (err) =>
  err?.body?.error || err?.body?.message || err?.message || 'Something went wrong';

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64);

const EMPTY_FORM = { tipId: '', title: '', body: '', linkPath: '', order: 1, enabled: true, isNew: true };

const TipsManagerCard = () => {
  const { showSuccess, showError } = useToast();
  const [tips, setTips] = useState([]);
  const [stats, setStats] = useState({ totalUsers: 0, dismissedByTip: {} });
  const [config, setConfig] = useState({ displaySeconds: 30, intervalMinutes: 60, emailSubject: '' });
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiGlobal.get('/admin/tips');
      setTips(res.tips || []);
      setStats(res.stats || { totalUsers: 0, dismissedByTip: {} });
      if (res.config) {
        setConfig({
          displaySeconds: res.config.displaySeconds ?? 30,
          intervalMinutes: res.config.intervalMinutes ?? 60,
          emailSubject: res.config.emailSubject?.['en-US'] || '',
        });
      }
    } catch (err) {
      showError(errMessage(err));
    } finally {
      setLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveTip = async () => {
    const tipId = form.isNew ? slugify(form.tipId || form.title) : form.tipId;
    if (!tipId) { showError('A tip id is required'); return; }
    setBusy(true);
    try {
      await apiGlobal.put(`/admin/tips/${encodeURIComponent(tipId)}`, {
        title: form.title.trim(),
        body: form.body.trim(),
        linkPath: form.linkPath.trim() || null,
        order: Number(form.order),
        enabled: form.enabled,
      });
      showSuccess(`Saved ${tipId} (translated to all locales)`);
      setForm(null);
      await load();
    } catch (err) {
      showError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleEnabled = async (tip) => {
    const en = tip.copy?.['en-US'] || {};
    setBusy(true);
    try {
      await apiGlobal.put(`/admin/tips/${encodeURIComponent(tip.tipId)}`, {
        title: en.title || '',
        body: en.body || '',
        linkPath: tip.linkPath || null,
        order: tip.order ?? 0,
        enabled: !tip.enabled,
      });
      await load();
    } catch (err) {
      showError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const deleteTip = async (tipId) => {
    setBusy(true);
    try {
      await apiGlobal.delete(`/admin/tips/${encodeURIComponent(tipId)}`);
      showSuccess(`Deleted ${tipId}`);
      setConfirmDelete(null);
      await load();
    } catch (err) {
      showError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const resetDismissals = async () => {
    setBusy(true);
    try {
      const res = await apiGlobal.post('/admin/tips/reset-dismissals', {});
      showSuccess(`Cleared dismissals for ${res.cleared} user${res.cleared === 1 ? '' : 's'}`);
      setConfirmReset(false);
      await load();
    } catch (err) {
      showError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const saveSettings = async () => {
    setBusy(true);
    try {
      await apiGlobal.put('/admin/tips/config', {
        displaySeconds: Number(config.displaySeconds),
        intervalMinutes: Number(config.intervalMinutes),
        emailSubject: config.emailSubject.trim(),
      });
      showSuccess('Settings saved (subject translated to all locales)');
      await load();
    } catch (err) {
      showError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return null;

  return (
    <Stack spacing="13px">
    <SectionContainer
      title="Tips"
      category="interactive"
      count={tips.length}
      onAdd={() => setForm({ ...EMPTY_FORM, order: tips.length + 1 })}
    >
      <Stack spacing={1} sx={{ pt: 1 }}>
        {tips.length === 0 && (
          <Typography sx={{ color: 'text.secondary', fontSize: 14, py: 1 }}>
            No tips configured.
          </Typography>
        )}
        {tips.map((tip) => (
          <Box
            key={tip.tipId}
            data-testid={`tip-row-${tip.tipId}`}
            sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}
          >
            <Switch
              size="small"
              checked={!!tip.enabled}
              onChange={() => toggleEnabled(tip)}
              disabled={busy}
              slotProps={{ input: { 'aria-label': `enable ${tip.tipId}` } }}
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'text.primary' }} noWrap>
                {tip.copy?.['en-US']?.title || tip.tipId}
              </Typography>
              <Typography sx={{ fontSize: '12px', color: 'text.secondary' }} noWrap>
                {tip.tipId} · order {tip.order ?? 0}{tip.linkPath ? ` · ${tip.linkPath}` : ''}
              </Typography>
            </Box>
            <Typography
              sx={{ fontSize: '13px', color: 'text.secondary', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
              title="users who disabled this message / total users"
            >
              {stats.dismissedByTip[tip.tipId] || 0}/{stats.totalUsers}
            </Typography>
            <IconButton
              size="small"
              aria-label={`edit ${tip.tipId}`}
              disabled={busy}
              onClick={() => setForm({
                tipId: tip.tipId,
                title: tip.copy?.['en-US']?.title || '',
                body: tip.copy?.['en-US']?.body || '',
                linkPath: tip.linkPath || '',
                order: tip.order ?? 0,
                enabled: !!tip.enabled,
                isNew: false,
              })}
              sx={{ color: 'text.secondary', p: '2px' }}
            >
              <IconPencil size={15} />
            </IconButton>
            <IconButton
              size="small"
              aria-label={`delete ${tip.tipId}`}
              disabled={busy}
              onClick={() => setConfirmDelete(tip.tipId)}
              sx={{ color: 'text.secondary', p: '2px' }}
            >
              <IconTrash size={15} />
            </IconButton>
          </Box>
        ))}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 1.5 }} alignItems={{ sm: 'center' }}>
          <TextField
            size="small" label="Display seconds" type="number" sx={{ width: 180 }}
            value={config.displaySeconds}
            onChange={(e) => setConfig((c) => ({ ...c, displaySeconds: e.target.value }))}
          />
          <TextField
            size="small" label="Interval minutes" type="number" sx={{ width: 180 }}
            value={config.intervalMinutes}
            onChange={(e) => setConfig((c) => ({ ...c, intervalMinutes: e.target.value }))}
          />
          <Button variant="contained" size="small" onClick={saveSettings} disabled={busy}>
            Save settings
          </Button>
        </Stack>

        <Box>
          <Button
            size="small"
            color="error"
            startIcon={<IconRestore size={15} />}
            onClick={() => setConfirmReset(true)}
            disabled={busy}
          >
            Reset all dismissals
          </Button>
        </Box>
      </Stack>

      <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{form?.isNew ? 'New tip' : `Edit ${form?.tipId}`}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {form?.isNew && (
              <TextField
                size="small" label="Tip id (slug, auto from title if blank)"
                value={form?.tipId || ''}
                onChange={(e) => setForm((f) => ({ ...f, tipId: e.target.value }))}
              />
            )}
            <TextField
              size="small" label="Title (English)"
              value={form?.title || ''}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            />
            <TextField
              size="small" label="Body (English)" multiline minRows={2}
              value={form?.body || ''}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
            />
            <TextField
              size="small" label="Link path (optional, e.g. /snapshots)"
              value={form?.linkPath || ''}
              onChange={(e) => setForm((f) => ({ ...f, linkPath: e.target.value }))}
            />
            <TextField
              size="small" label="Order" type="number" sx={{ width: 120 }}
              value={form?.order ?? 0}
              onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForm(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={saveTip} disabled={busy}>
            Save and translate
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => !busy && setConfirmDelete(null)}>
        <DialogTitle>Delete tip</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`Delete ${confirmDelete}? Users will no longer see this message.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} disabled={busy}>Cancel</Button>
          <Button color="error" onClick={() => deleteTip(confirmDelete)} disabled={busy}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmReset} onClose={() => !busy && setConfirmReset(false)}>
        <DialogTitle>Reset all dismissals</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Every user who disabled a message will start seeing the rotation
            again. This is a mass write and cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReset(false)} disabled={busy}>Cancel</Button>
          <Button color="error" onClick={resetDismissals} disabled={busy}>Reset</Button>
        </DialogActions>
      </Dialog>
    </SectionContainer>

    {/* The summary-email subject is snapshot-email config, not a tip - it
        gets its own card so the two concerns read as separate (#1720). */}
    <SectionContainer title="Email Summary Config" category="budgets">
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 1 }} alignItems={{ sm: 'center' }}>
        <TextField
          size="small" label="Email subject (English)" sx={{ flex: 1, minWidth: 220 }}
          value={config.emailSubject}
          onChange={(e) => setConfig((c) => ({ ...c, emailSubject: e.target.value }))}
        />
        <Button variant="contained" size="small" onClick={saveSettings} disabled={busy}>
          Save subject
        </Button>
      </Stack>
    </SectionContainer>
    </Stack>
  );
};

export default TipsManagerCard;
